import axios from "axios";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Stage 4E: access token SOLO in memoria; il refresh token vive in un cookie HttpOnly.
let accessToken = null;
let expiresAt = 0; // epoch secondi
let refreshTimer = null;
let refreshInflight = null;
let onSessionEnd = () => {
  if (window.location.pathname !== "/login") window.location.href = "/login";
};

export const getAccessToken = () => accessToken;
export const setSessionEndHandler = (fn) => {
  onSessionEnd = fn;
};

const CSRF = { "X-Maven-CSRF": "1" };
const AUTH_PATHS = ["/auth/login", "/auth/refresh", "/auth/logout"];
const isAuthPath = (url = "") => AUTH_PATHS.some((p) => url.endsWith(p));
const REFRESH_MARGIN_S = 120;
const RETRY_DELAYS_MS = [5000, 15000, 45000];

function scheduleRefresh(attempt = 0) {
  clearTimeout(refreshTimer);
  if (!accessToken || !expiresAt) return;
  const ms = attempt > 0
    ? RETRY_DELAYS_MS[attempt - 1]
    : Math.min(Math.max((expiresAt - REFRESH_MARGIN_S) * 1000 - Date.now(), 5000), 2147483647);
  refreshTimer = setTimeout(() => {
    refreshSession().catch((e) => {
      if (isTransient(e) && attempt < RETRY_DELAYS_MS.length) scheduleRefresh(attempt + 1);
    });
  }, ms);
}

export function setSession(data) {
  accessToken = data?.access_token || null;
  expiresAt = data?.expires_at || 0;
  scheduleRefresh();
}

export function clearSession() {
  accessToken = null;
  expiresAt = 0;
  clearTimeout(refreshTimer);
  refreshTimer = null;
}

export const isTransient = (e) => !e?.response || e.response.status >= 500 || e.response.status === 408;
const isSessionDead = (e) => [401, 403].includes(e?.response?.status);

// Un solo rinnovo alla volta per scheda; il backend deduplica anche tra schede.
export function refreshSession() {
  if (!refreshInflight) {
    refreshInflight = api
      .post("/auth/refresh", null, { headers: CSRF, __skipAuth: true })
      .then((r) => {
        setSession(r.data);
        window.dispatchEvent(new CustomEvent("maven:session", { detail: r.data.user }));
        return r.data;
      })
      .finally(() => {
        refreshInflight = null;
      });
  }
  return refreshInflight;
}

export async function logoutRequest() {
  try {
    await api.post("/auth/logout", null, { headers: CSRF, __skipAuth: false, timeout: 5000 });
  } catch {
    // il logout locale avviene comunque
  } finally {
    clearSession();
  }
}

const expiringSoon = () => accessToken && expiresAt && expiresAt * 1000 - Date.now() < REFRESH_MARGIN_S * 1000;
if (typeof document !== "undefined") {
  const wake = () => {
    if (document.visibilityState === "visible" && expiringSoon()) refreshSession().catch(() => {});
  };
  document.addEventListener("visibilitychange", wake);
  window.addEventListener("online", wake);
}

api.interceptors.request.use((cfg) => {
  if (accessToken && !cfg.__skipAuth) cfg.headers.Authorization = `Bearer ${accessToken}`;
  return cfg;
});

// Retry-once helper for transient 5xx
async function retryOnce(err) {
  const cfg = err.config;
  if (!cfg || cfg.__retried) return Promise.reject(err);
  cfg.__retried = true;
  await new Promise((r) => setTimeout(r, 300));
  return api(cfg);
}

function endSession() {
  clearSession();
  onSessionEnd();
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err.response?.status;
    const cfg = err.config || {};
    if (status === 401 && !isAuthPath(cfg.url) && accessToken !== null) {
      if (cfg.__authRetried) {
        endSession();
        return Promise.reject(err);
      }
      try {
        await refreshSession();
      } catch (e) {
        if (isSessionDead(e)) endSession();
        else toast.error("Impossibile rinnovare la sessione. Riprova tra poco.");
        return Promise.reject(err);
      }
      cfg.__authRetried = true;
      return api(cfg);
    }
    if (status === 401 && !isAuthPath(cfg.url)) {
      endSession();
      return Promise.reject(err);
    }
    if (status === 403 && err.response?.data?.detail === "Account disattivato" && !isAuthPath(cfg.url)) {
      endSession();
      return Promise.reject(err);
    }
    if (["get", "head"].includes((cfg.method || "get").toLowerCase()) &&
        (status >= 500 || !err.response) && !cfg.__retried) {
      try {
        return await retryOnce(err);
      } catch (e) {
        toast.error("Errore temporaneo del server. Riprova.");
        return Promise.reject(e);
      }
    }
    return Promise.reject(err);
  }
);

export function apiError(error, fallback = "Operazione non riuscita") {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (uncertainMutation(error) && error.config && !["get", "head"].includes((error.config.method || "get").toLowerCase()))
    return "Esito non confermato: l'operazione potrebbe essere stata salvata. Non ripetere automaticamente l'invio.";
  return error.message && !error.config ? error.message : fallback;
}
// A proxy can lose the post-commit response and return its own 5xx/408 body.
// Absence of our JSON envelope is not proof that the mutation rolled back.
export const uncertainMutation = (error) => !!error.response?.data?.detail?.outcome_unknown ||
  !error.response || error.response.status >= 500 || error.response.status === 408;
